import { queryOptions, useQuery } from '@tanstack/react-query';

import { EdgeConfiguration } from '@cast/types-omni';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const EDGE_CONFIGURATIONS_QUERY_KEY = 'edge-configurations';

export const getEdgeConfigurationsQueryKey = ({
  organizationId,
  clusterId,
  edgeLocationId,
}: {
  organizationId?: string;
  clusterId?: string;
  edgeLocationId?: string;
}) => [
  EDGE_CONFIGURATIONS_QUERY_KEY,
  ...(organizationId ? [organizationId] : []),
  ...(clusterId ? [clusterId] : []),
  ...(edgeLocationId ? [edgeLocationId] : []),
];

const getEdgeConfigurationsQueryOptions = ({
  clusterId,
  edgeLocationId,
  organizationId,
}: {
  clusterId: string | undefined;
  edgeLocationId: string | undefined;
  organizationId: string | undefined;
}) =>
  queryOptions({
    queryKey: getEdgeConfigurationsQueryKey({
      organizationId,
      clusterId,
      edgeLocationId,
    }),
    queryFn: async () => {
      const { data } = await apiClient.omni.listEdgeConfigurations({
        organizationId: organizationId!,
        clusterId: clusterId!,
        ...(edgeLocationId && { edgeLocationId }),
      });
      return data?.items || [];
    },
    enabled: !!clusterId && !!organizationId,
  });

type UseEdgeConfigurations = {
  isLoading: boolean;
  error?: any;
  edgeConfigurations: EdgeConfiguration[];
  refetch: () => void;
};

export const useListEdgeConfigurationsQuery = ({
  clusterId,
  edgeLocationId,
}: {
  clusterId: string | undefined;
  edgeLocationId?: string;
}): UseEdgeConfigurations => {
  const { currentOrganization } = useOrganizations();
  const {
    isLoading,
    data: edgeConfigurations,
    error,
    refetch,
  } = useQuery(
    getEdgeConfigurationsQueryOptions({
      clusterId,
      edgeLocationId,
      organizationId: currentOrganization?.id,
    })
  );

  return {
    isLoading,
    edgeConfigurations: edgeConfigurations || [],
    error,
    refetch,
  };
};
